import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, notification } from "antd";
import { CheckCircleTwoTone, WarningTwoTone } from "@ant-design/icons";
import {
  paymentMethodsArray,
  getMethodImage,
  mbwayAboveLimit,
  haveInstallments,
  paymentMethodValid,
  allowKlarnaMethod,
  allowCofidisPayMethod,
} from "../../utils/helper";

const PaymentMethod = ({ data, actionHandler, selected, customStyle }) => {
  const { t } = useTranslation();
  const accentColor = customStyle?.primary?.backgroundColor || "#52c41a";
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(data?.payment_method?.method_id);
  const isMBWAYAboveLimit = mbwayAboveLimit(data);
  const allowKlarna = allowKlarnaMethod(data);
  const allowCofidisPay = allowCofidisPayMethod(data);
  const purchaseHaveInstallments = haveInstallments(data.purchase_products);

  const gridStyle = {
    width: "100%",
    textAlign: "center",
    cursor: "pointer",
  };

  // Filter payment methods based on allowed methods, and exclude if based on data.hide_method (array)
  const filteredPaymentMethods = paymentMethodsArray(t).filter(
    (method) => data.allowed_methods.includes(method.method_id) && !data?.hide_method?.includes(method.method_id),
  );

  useEffect(() => {
    // Check if selected payment method is still available, if not, reset it
    if (filteredPaymentMethods.find((method) => method.method_id === selectedPaymentMethod) === undefined) {
      setSelectedPaymentMethod(undefined);
    }
  }, [data]);

  const getMethodData = (method_id) => filteredPaymentMethods.find((method) => method.method_id === method_id);

  const selectPaymentMethod = (method_id) => {
    // Block selection of an unavailable method (e.g. MB WAY above the max amount)
    const validation = validatePaymentMethod(method_id);
    if (!validation.status) {
      notification.error({
        message: t("options.paymentMethodUnavailable"),
        description: validation.data,
      });
      return;
    }
    setSelectedPaymentMethod(method_id);
    selected(method_id);
    actionHandler("payment_method", getMethodData(method_id));
  };

  const ItemIcon = () => {
    //mbwayAboveLimit
    if (isMBWAYAboveLimit) {
      return <WarningTwoTone twoToneColor={"#eed202"} />;
    } else {
      return <CheckCircleTwoTone twoToneColor={accentColor} />;
    }
  };

  const validatePaymentMethod = (method_id = undefined) => paymentMethodValid(data, method_id, t);
  const SmallWarning = ({ method_id }) => {
    if (!validatePaymentMethod(method_id).status) {
      return <small style={{ display: "block", marginTop: 2 }}>({validatePaymentMethod(method_id).data})</small>;
    }

    return <></>;
  };
  const PaymentMethodComponent = () => {
    return filteredPaymentMethods.map((method) => {
      // Hide PayPal if have installments
      if (method.method_id === "paypal" && purchaseHaveInstallments) return null;

      // Hide Klarna if have installments
      if (method.method_id === "klarna" && purchaseHaveInstallments) return null;

      // Hide Klarna if not allowed (price range 50-5000)
      if (allowKlarna === false && method.method_id === "klarna") return null;

      // Hide CofidisPay if not allowed (price range 60-2500)
      if (allowCofidisPay === false && method.method_id === "cofidispay") return null;
      if (method.method_id === "cofidispay" && purchaseHaveInstallments === true) return null;

      return (
        <Card.Grid
          key={method.method_id}
          style={gridStyle}
          className={selectedPaymentMethod === method.method_id ? (validatePaymentMethod(method.method_id).status ? "card-selected" : "card-warning") : "card"}
          onClick={() => selectPaymentMethod(method.method_id)}
        >
          <div className="flex-space-between">
            <div className="card-name">
              {selectedPaymentMethod === method.method_id && <ItemIcon />}
              <span
                style={{
                  paddingLeft: selectedPaymentMethod === method.method_id ? 10 : 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                {method.name}
                <SmallWarning method_id={method.method_id} />
              </span>
            </div>
            <div className="card-image">
              <img height={30} src={getMethodImage(method.method_id)} alt={method.name} />
            </div>
          </div>
        </Card.Grid>
      );
    });
  };

  return <PaymentMethodComponent />;
};

export default PaymentMethod;
